import React, { useState, useEffect } from 'react';
import { FiCalendar, FiTrendingUp, FiDollarSign, FiShoppingBag } from 'react-icons/fi';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const SalesAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [groupBy, setGroupBy] = useState('day');

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange, groupBy]);

  const fetchAnalytics = async () => {
    try {
      const params = {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        groupBy
      };

      const response = await api.get('/superadmin/analytics/sales', { params });
      if (response.data.success) {
        setAnalytics(response.data.data);
      }
    } catch (error) {
      toast.error('Failed to fetch analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loading"><div className="spinner"></div></div>;
  }

  return (
    <div className="sales-analytics-page">
      <div className="page-header">
        <h1>Sales Reports & Analytics</h1>
        <p>Comprehensive sales analysis and vendor performance</p>
      </div>

      {/* Date Range Filter */}
      <div className="filters-section">
        <div className="filter-row">
          <div className="date-inputs">
            <label>Start Date:</label>
            <input
              type="date"
              className="form-control"
              value={dateRange.startDate}
              onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
            />
            <label>End Date:</label>
            <input
              type="date"
              className="form-control"
              value={dateRange.endDate}
              onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
            />
          </div>

          <select
            className="form-control"
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value)}
          >
            <option value="day">Daily</option>
            <option value="week">Weekly</option>
            <option value="month">Monthly</option>
          </select>
        </div>
      </div>

      {/* Summary Cards */}
      {analytics?.summary && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: '#e3f2fd' }}>
              <FiDollarSign style={{ color: '#1976d2' }} />
            </div>
            <div className="stat-content">
              <h3>₹{analytics.summary.totalRevenue.toLocaleString()}</h3>
              <p>Total Revenue</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: '#e8f5e9' }}>
              <FiShoppingBag style={{ color: '#388e3c' }} />
            </div>
            <div className="stat-content">
              <h3>{analytics.summary.totalOrders}</h3>
              <p>Total Orders</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: '#f3e5f5' }}>
              <FiTrendingUp style={{ color: '#7b1fa2' }} />
            </div>
            <div className="stat-content">
              <h3>₹{Math.round(analytics.summary.averageOrderValue).toLocaleString()}</h3>
              <p>Average Order Value</p>
            </div>
          </div>
        </div>
      )}

      {/* Vendor Performance */}
      <div className="section">
        <h2>Vendor Performance</h2>
        <div className="table-section">
          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Vendor Name</th>
                  <th>Total Orders</th>
                  <th>Completed Orders</th>
                  <th>Total Revenue</th>
                  <th>Avg Order Value</th>
                  <th>Completion Rate</th>
                </tr>
              </thead>
              <tbody>
                {analytics?.vendorSales.map((vendor, index) => (
                  <tr key={index}>
                    <td><strong>{vendor.vendorName}</strong></td>
                    <td>{vendor.totalOrders}</td>
                    <td>{vendor.completedOrders}</td>
                    <td><strong>₹{vendor.totalRevenue.toLocaleString()}</strong></td>
                    <td>₹{Math.round(vendor.totalRevenue / vendor.totalOrders).toLocaleString()}</td>
                    <td>
                      <span className={`badge ${vendor.completedOrders / vendor.totalOrders > 0.8 ? 'badge-success' : 'badge-warning'}`}>
                        {Math.round((vendor.completedOrders / vendor.totalOrders) * 100)}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {analytics?.vendorSales.length === 0 && (
            <div className="empty-state">
              <p>No sales data available for selected period</p>
            </div>
          )}
        </div>
      </div>

      {/* Daily Sales Trend */}
      {analytics?.dailySales && analytics.dailySales.length > 0 && (
        <div className="section">
          <h2>Sales Trend</h2>
          <div className="table-section">
            <div className="table-responsive">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Orders</th>
                    <th>Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.dailySales.map((day, index) => (
                    <tr key={index}>
                      <td>{new Date(day.date).toLocaleDateString()}</td>
                      <td>{day.orders}</td>
                      <td><strong>₹{day.revenue.toLocaleString()}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesAnalytics;
